import type { GoalDraft, GoalItem } from "@/lib/api";

type GoalExecutionInput = {
  title?: string | null;
  description?: string | null;
  category?: string | null;
  suggestedSessionMinutes?: number | null;
};

export type GoalExecutionStyle = {
  isCadenceGoal: boolean;
  label: string;
  cadenceChip: string | null;
  hint: string;
  nextActionLabel: string;
};

const CADENCE_SIGNAL =
  /\b(every day|every morning|every night|each day|each week|weekly|daily|habit|routine|practice|practise|training|workout|exercise|meditation|sleep|study|studying|lesson|session|sessions|streak|consistent|consistently|piano|guitar|run|running|walk|walking|journal|reading)\b/i;
const PROJECT_SIGNAL =
  /\b(launch|ship|build|finish|create|publish|portfolio|project|report|presentation|deliver|deploy)\b/i;

export function describeGoalExecutionStyle({
  title,
  description,
  category,
  suggestedSessionMinutes,
}: GoalExecutionInput): GoalExecutionStyle {
  const normalized = `${title || ""} ${description || ""}`.toLowerCase();
  const normalizedCategory = (category || "").toLowerCase();
  const categoryLeansCadence =
    normalizedCategory === "learning" || normalizedCategory === "health";
  const textLeansCadence = CADENCE_SIGNAL.test(normalized);
  const textLeansProject = PROJECT_SIGNAL.test(normalized);
  const isCadenceGoal = textLeansCadence || (categoryLeansCadence && !textLeansProject);

  if (isCadenceGoal) {
    return {
      isCadenceGoal: true,
      label: "Roadmap + cadence",
      cadenceChip: suggestedSessionMinutes ? `${suggestedSessionMinutes}m rhythm` : "Repeatable rhythm",
      hint: "Treat this like a repeatable rhythm. Let milestones track progress, then place one session at a time.",
      nextActionLabel: "Plan next session",
    };
  }

  return {
    isCadenceGoal: false,
    label: "Roadmap + milestones",
    cadenceChip: suggestedSessionMinutes ? `${suggestedSessionMinutes}m focus block` : null,
    hint: "Use milestones to make the goal concrete, then pull the next visible step into your day.",
    nextActionLabel: "Plan next step",
  };
}

export function describeGoalDraftExecutionStyle(
  draft: Pick<GoalDraft, "title" | "description" | "category" | "suggested_session_minutes">
): GoalExecutionStyle {
  return describeGoalExecutionStyle({
    title: draft.title,
    description: draft.description,
    category: draft.category,
    suggestedSessionMinutes: draft.suggested_session_minutes,
  });
}

export function describeStoredGoalExecutionStyle(
  goal: Pick<GoalItem, "title" | "description" | "category">
): GoalExecutionStyle {
  return describeGoalExecutionStyle({
    title: goal.title,
    description: goal.description,
    category: goal.category,
  });
}
